import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { AlertTriangle, CalendarDays, Camera, Check, CheckCircle2, ChevronLeft, ChevronRight, Clock3, FilePenLine, LogIn, LogOut, MapPin, RefreshCw, ScanFace, ShieldCheck, Smartphone, X } from 'lucide-react';
import { get, patch, post } from '../api';
import { useApp } from '../AppContext';
import { Button, Card, ErrorState, Field, FormGrid, Loading, Modal, PageHeader, SectionHeader, Status, formatDate, useModalScrollLock } from '../components';
import {
  FaceScannerTimeoutError,
  cameraErrorMessage,
  cameraFacingFromLabel,
  faceScannerFailureCode,
  faceFrameQuality,
  loadFaceScannerModels,
  locationErrorMessage,
  rankVideoInputs,
  resetFaceScannerModels,
  waitForVideoReady,
  withScannerTimeout,
} from '../face-scanner';
import {
  clearAttendanceReminder,
  listenForMonitoringStatus,
  monitoringBridgeAvailable,
  openUsageAccessSettings,
  requestMonitoringNotifications,
  requestMonitoringRefresh,
  requestNativeMonitoringStatus,
} from '../device-monitoring-bridge';
import { WebDateField, WebDateTimeField } from '../SafeDateTimeFields';
import { AttendanceRangeToolbar, AttendanceSummaryStrip, AttendanceRecordsPanel, AttendanceCorrectionsPanel, AttendanceDiagnosticsPanel, attendanceToday } from '../AttendanceWorkspace';

type AttendanceItem = Record<string, string | number | null>;
type Correction = Record<string, string | number | null>;
type AttendanceCalendarDay = { date: string; status: string; label: string; startTime?: string; endTime?: string; sessionId?: number | null };
type AttendanceCalendarData = { month: string; employee: { id: number; name: string }; days: AttendanceCalendarDay[] };
type MonitoringState = {
  ready: boolean;
  shouldMonitor: boolean;
  disclosure: { version: string; title: string; summary: string; exclusions: string; timing: string };
  device: null | { id: number; deviceName?: string | null; platform: string; companyOwned: boolean; monitoringEnabled: boolean;
    status: string; usageAccessGranted: boolean; notificationPermissionGranted: boolean; disclosureVersion?: string | null; disclosureAcceptedAt?: string | null; serviceRunning: boolean };
  session: null | { id: number; status: string; scheduledEndAt: string };
};

function localDateTimeValue(value: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function AttendancePage() {
  const { bootstrap } = useApp();
  const [items, setItems] = useState<AttendanceItem[]>([]);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [tab, setTab] = useState<'records' | 'corrections' | 'calendar' | 'diagnostics'>('records');
  const [ownItems, setOwnItems] = useState<AttendanceItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const loadRequest = useRef(0);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [faceEnrolled, setFaceEnrolled] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState<'face' | 'button'>('face');
  const [faceOpen, setFaceOpen] = useState(false);
  const [faceMode, setFaceMode] = useState<'enroll' | 'checkin' | 'checkout'>('enroll');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionDefaults, setCorrectionDefaults] = useState<{ date: string; checkinAt?: string; checkoutAt?: string; sessionId?: number | null } | null>(null);
  const [unresolvedPrevious, setUnresolvedPrevious] = useState<AttendanceItem[]>([]);
  const [calendar, setCalendar] = useState<AttendanceCalendarData | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => attendanceToday().slice(0, 7));
  const [calendarView, setCalendarView] = useState<'month' | 'week'>('month');
  const [pendingFaceToken, setPendingFaceToken] = useState('');
  const [from, setFrom] = useState(() => new Date(Date.parse(`${attendanceToday()}T12:00:00Z`) - 29 * 86_400_000).toISOString().slice(0, 10));
  const [to, setTo] = useState(attendanceToday);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [monitoring, setMonitoring] = useState<MonitoringState | null>(null);
  const [monitoringBusy, setMonitoringBusy] = useState(false);
  const hasEmployee = Boolean(bootstrap?.user.employee_id);
  const canManage = Boolean(bootstrap?.capabilities.admin || bootstrap?.capabilities.manageTeam || bootstrap?.capabilities.managePayroll);

  const canSeeDiagnostics = Boolean(bootstrap?.capabilities.admin || bootstrap?.capabilities.manageTeam);
  const ownEmployeeId = bootstrap?.user.employee_id || 0;
  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    try {
      const today = attendanceToday();
      const [attendance, correctionData, calendarData, ownData, face] = await Promise.all([
        get<{ items: AttendanceItem[]; summary: Record<string, number> }>(`/api/attendance?from=${from}&to=${to}`),
        get<{ items: Correction[] }>('/api/attendance/corrections'),
        hasEmployee ? get<AttendanceCalendarData>(`/api/attendance/calendar?month=${calendarMonth}`) : Promise.resolve(null),
        hasEmployee ? get<{ items: AttendanceItem[]; unresolvedPreviousSessions: AttendanceItem[] }>(`/api/attendance?employeeId=${ownEmployeeId}&from=${today}&to=${today}`)
          : Promise.resolve({ items: [], unresolvedPreviousSessions: [] }),
        hasEmployee ? get<{ enrolled: boolean; attendanceMode?: 'face' | 'button' }>('/api/face/profile') : Promise.resolve({ enrolled: false, attendanceMode: 'face' as const }),
      ]);
      if (request !== loadRequest.current) return;
      setItems(attendance.items); setSummary(attendance.summary); setCorrections(correctionData.items);
      setUnresolvedPrevious(ownData.unresolvedPreviousSessions || []); setOwnItems(ownData.items);
      setCalendar(calendarData); setFaceEnrolled(face.enrolled); setError('');
      setAttendanceMode(face.attendanceMode === 'button' ? 'button' : 'face');
    } catch (requestError) { if (request === loadRequest.current) setError((requestError as Error).message); }
    finally { if (request === loadRequest.current) { setLoading(false); setHasLoaded(true); } }
  }, [calendarMonth, from, hasEmployee, ownEmployeeId, to]);
  useEffect(() => { void load(); return () => { loadRequest.current += 1; }; }, [load]);
  useEffect(() => { if (hasEmployee && hasLoaded && attendanceMode === 'face') void loadFaceScannerModels().catch(() => undefined); }, [hasEmployee, hasLoaded, attendanceMode]);
  const loadMonitoring = useCallback(async () => {
    if (!hasEmployee) return;
    try { setMonitoring(await get<MonitoringState>('/api/device-monitoring/me')); }
    catch { setMonitoring(null); }
  }, [hasEmployee]);
  useEffect(() => {
    void loadMonitoring();
    const stop = listenForMonitoringStatus(() => { void loadMonitoring(); });
    if (monitoringBridgeAvailable()) requestNativeMonitoringStatus();
    return stop;
  }, [loadMonitoring]);

  if (loading && !hasLoaded) return <Loading label="Loading attendance" />;
  const ownToday = hasEmployee ? ownItems.find((item) => Number(item.employee_id) === Number(bootstrap?.user.employee_id)
    && item.attendance_date === attendanceToday()) : null;
  const checkedIn = Boolean(ownToday && !ownToday.checkout_at);

  function startAttendance(action: 'checkin' | 'checkout') {
    if (action === 'checkin' && monitoring?.device?.companyOwned && monitoring.device.monitoringEnabled && !monitoring.ready) {
      setError('Complete the company-phone monitoring setup below before checking in.');
      return;
    }
    if (attendanceMode === 'button') { void recordButtonAttendance(action); return; }
    setFaceMode(action); setFaceOpen(true);
  }

  async function recordButtonAttendance(action: 'checkin' | 'checkout') {
    setBusy(true); setError('');
    try {
      await post(`/api/attendance/${action === 'checkin' ? 'check-in' : 'check-out'}`, {});
      if (action === 'checkout') clearAttendanceReminder();
      requestMonitoringRefresh(); await Promise.all([load(), loadMonitoring()]);
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(false); }
  }

  async function acceptMonitoringDisclosure() {
    if (!monitoring?.device) return;
    setMonitoringBusy(true);
    try {
      const updated = await post<MonitoringState>(`/api/device-monitoring/devices/${monitoring.device.id}/disclosure`, {
        accepted: true, version: monitoring.disclosure.version,
      });
      setMonitoring(updated);
      requestMonitoringRefresh();
      if (!updated.device?.usageAccessGranted && monitoringBridgeAvailable()) openUsageAccessSettings();
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setMonitoringBusy(false); }
  }

  async function onFaceComplete(token?: string) {
    setFaceOpen(false);
    if (faceMode === 'enroll') { setFaceEnrolled(true); await load(); return; }
    if (faceMode === 'checkout') { setPendingFaceToken(token || ''); setCheckoutOpen(true); return; }
    setBusy(true);
    try { await post('/api/attendance/check-in', { faceToken: token }); requestMonitoringRefresh(); await Promise.all([load(), loadMonitoring()]); }
    catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(false); }
  }

  async function checkout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await post('/api/attendance/check-out', { faceToken: pendingFaceToken, workLog: values.workLog,
        completedItems: values.completedItems, pendingItems: values.pendingItems, blockers: values.blockers });
      clearAttendanceReminder(); requestMonitoringRefresh(); setCheckoutOpen(false); await Promise.all([load(), loadMonitoring()]);
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(false); }
  }

  async function requestCorrection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); const form = new FormData(event.currentTarget);
    const checkin = String(form.get('checkinAt') || ''); const checkoutAt = String(form.get('checkoutAt') || '');
    try {
      await post('/api/attendance/corrections', { date: form.get('date'),
        attendanceSessionId: correctionDefaults?.sessionId || null,
        checkinAt: checkin ? new Date(checkin).toISOString() : null,
        checkoutAt: checkoutAt ? new Date(checkoutAt).toISOString() : null, reason: form.get('reason') });
      setCorrectionOpen(false); setCorrectionDefaults(null); await load();
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(false); }
  }

  function openCorrection(item?: AttendanceItem | AttendanceCalendarDay) {
    const record = (item || {}) as Record<string, string | number | null | undefined>;
    const date = String(record.attendance_date || record.date || new Date().toLocaleDateString('en-CA'));
    const checkinAt = record.checkin_at ? localDateTimeValue(String(record.checkin_at)) : `${date}T09:00`;
    setCorrectionDefaults({ date, checkinAt, checkoutAt: `${date}T18:00`, sessionId: Number(record.id || record.sessionId || 0) || null });
    setCorrectionOpen(true);
  }

  function moveCalendarMonth(offset: number) {
    const [year, month] = calendarMonth.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1 + offset, 1));
    setCalendarMonth(next.toISOString().slice(0, 7));
  }

  async function decideCorrection(id: number, status: 'approved' | 'rejected') {
    setBusy(true);
    try { await patch(`/api/attendance/corrections/${id}`, { status }); await load(); }
    catch (requestError) { setError((requestError as Error).message); }
    finally { setBusy(false); }
  }

  return <div className="attendance-workspace"><PageHeader eyebrow="People" title="Attendance" description="Review attendance, check-in times and correction requests."
    actions={hasEmployee ? <Button variant="secondary" icon={FilePenLine} onClick={() => openCorrection()}>Request correction</Button> : undefined} />
    {error && <ErrorState message={error} />}
    {hasEmployee && unresolvedPrevious.length > 0 && <Card className="attendance-unresolved"><AlertTriangle size={22} /><div><span className="eyebrow">Previous attendance needs correction</span><h2>Your new check-in is available</h2><p>{unresolvedPrevious.length} earlier {unresolvedPrevious.length === 1 ? 'day has' : 'days have'} no check-out. The records remain preserved and do not block today. Submit the correct time for manager approval.</p></div><Button variant="secondary" icon={FilePenLine} onClick={() => openCorrection(unresolvedPrevious[0])}>Correct {formatDate(String(unresolvedPrevious[0].attendance_date))}</Button></Card>}
    {hasEmployee && <details className="att-policy"><summary><AlertTriangle size={15}/><strong>Attendance policy</strong><span>Late check-in or missed check-out is recorded as a half day.</span><small>View details</small></summary><p>Check in by your scheduled start and check out before leaving. If checkout is still missing 15 minutes after shift end, Nosivo sends an urgent reminder and marks the day as half day. Genuine technical failures must be submitted as an attendance correction for administrator review.</p></details>}
    {hasEmployee && monitoring?.device?.companyOwned && monitoring.device.monitoringEnabled && <Card className={`attendance-monitoring ${monitoring.shouldMonitor ? 'attendance-monitoring--active' : ''}`}>
      <span className="attendance-monitoring__icon"><Smartphone size={22} /></span>
      <div className="attendance-monitoring__copy"><span className="eyebrow">Company-owned phone</span><h2>{monitoring.shouldMonitor ? 'Shift monitoring is active' : monitoring.ready ? 'Monitoring is ready for check-in' : 'Monitoring setup required'}</h2>
        <p>{monitoring.disclosure.summary}</p><small>{monitoring.disclosure.exclusions} {monitoring.disclosure.timing}</small>
        {monitoring.shouldMonitor && monitoring.session && <strong>Automatically stops by {formatDate(monitoring.session.scheduledEndAt, true)} or when you check out.</strong>}
      </div>
      <div className="attendance-monitoring__actions">
        {!monitoring.device.disclosureAcceptedAt && <Button icon={ShieldCheck} busy={monitoringBusy} onClick={() => void acceptMonitoringDisclosure()}>I understand and enable</Button>}
        {monitoring.device.disclosureAcceptedAt && !monitoring.device.usageAccessGranted && <Button icon={Smartphone} onClick={() => openUsageAccessSettings()}>Allow usage access</Button>}
        {monitoring.device.disclosureAcceptedAt && monitoring.device.usageAccessGranted && !monitoring.device.notificationPermissionGranted && <Button icon={Smartphone} onClick={() => requestMonitoringNotifications()}>Allow monitoring notification</Button>}
        {monitoring.ready && <Status value={monitoring.shouldMonitor ? 'monitoring active' : 'ready'} />}
        <small>A visible Android notification remains on while monitoring is active.</small>
      </div>
    </Card>}
    {hasEmployee && <div className="attendance-hero">
      <div className="attendance-hero__status"><span className={checkedIn ? 'attendance-orb attendance-orb--active' : 'attendance-orb'}><Clock3 size={25} /></span>
        <div><span className="eyebrow">Your attendance</span><h2>{checkedIn ? 'You are checked in' : ownToday?.checkout_at ? 'Your workday is complete' : 'Ready when you are'}</h2>
          <p>{checkedIn ? `Started ${formatDate(String(ownToday?.checkin_at), true)} · ${ownToday?.checkin_location}.` : ownToday?.checkout_at ? `${(Number(ownToday.work_minutes || 0) / 60).toFixed(1)} hours recorded today.` : attendanceMode === 'button' ? 'Use the button below to record your attendance.' : faceEnrolled ? 'Scan your face at a trusted work site to check in.' : 'Set up your employee Face ID before the first check-in.'}</p>
        </div>
      </div>
      <div className="attendance-hero__actions">
        {attendanceMode === 'face' && !faceEnrolled ? <Button icon={ScanFace} onClick={() => { setFaceMode('enroll'); setFaceOpen(true); }}>Set up Face ID</Button>
          : checkedIn ? <Button icon={LogOut} busy={busy} onClick={() => startAttendance('checkout')}>{attendanceMode === 'button' ? 'Sign out' : 'Scan to check out'}</Button>
          : ownToday?.checkout_at ? <Button variant="secondary" disabled icon={CheckCircle2}>Completed today</Button>
          : <Button icon={LogIn} busy={busy} onClick={() => startAttendance('checkin')}>{attendanceMode === 'button' ? 'Sign in' : 'Scan to check in'}</Button>}
        <span className={`face-ready ${attendanceMode === 'button' || faceEnrolled ? 'face-ready--yes' : ''}`}><ShieldCheck size={15} />{attendanceMode === 'button' ? 'Account sign-in attendance' : faceEnrolled ? 'Employee Face ID ready' : 'Enrollment required'}</span>
      </div>
    </div>}

    <AttendanceRangeToolbar from={from} to={to} loading={loading} onChange={(start, end) => { setFrom(start); setTo(end); }} onRefresh={() => void load()} />
    <AttendanceSummaryStrip summary={summary} pending={corrections.filter(item => item.status === 'pending').length} onCorrections={() => setTab('corrections')} />
    <Card className="att-main">
      <nav className="att-tabs" role="tablist" aria-label="Attendance views">
        {([
          { key: 'records', label: canManage ? 'Attendance records' : 'Your records', count: items.length },
          { key: 'corrections', label: 'Corrections', count: corrections.filter(item => item.status === 'pending').length },
          ...(hasEmployee ? [{ key: 'calendar', label: 'Your calendar' }] : []),
          ...(canSeeDiagnostics ? [{ key: 'diagnostics', label: 'Face ID checks' }] : []),
        ] as Array<{ key: typeof tab; label: string; count?: number }>).map(item =>
          <button key={item.key} id={`attendance-tab-${item.key}`} role="tab" aria-selected={tab === item.key} aria-controls="attendance-active-panel"
            className={tab === item.key ? 'is-active' : ''} onClick={() => setTab(item.key)}>
            {item.label}{item.count !== undefined && <span>{item.count}</span>}
          </button>)}
      </nav>
      <div role="tabpanel" id="attendance-active-panel" aria-labelledby={`attendance-tab-${tab}`} aria-busy={loading}>
        {tab === 'records' && <AttendanceRecordsPanel items={items} canManage={canManage} />}
        {tab === 'corrections' && <AttendanceCorrectionsPanel items={corrections} canManage={canSeeDiagnostics} busy={busy} onDecide={(id,status) => void decideCorrection(id,status)} />}
        {tab === 'diagnostics' && canSeeDiagnostics && <AttendanceDiagnosticsPanel from={from} to={to} />}
        {tab === 'calendar' && (loading ? <Loading label="Loading your attendance calendar" /> : <>
    {hasEmployee && calendar && <Card className="attendance-calendar-card"><div className="attendance-calendar-toolbar"><SectionHeader title={`${calendar.employee.name} · attendance calendar`} description="P = present, A = absent, plus pending corrections and check-ins/outs" /><div><Button size="sm" variant="ghost" icon={ChevronLeft} aria-label="Previous month" onClick={() => moveCalendarMonth(-1)} /><strong>{new Date(`${calendarMonth}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong><Button size="sm" variant="ghost" icon={ChevronRight} aria-label="Next month" onClick={() => moveCalendarMonth(1)} /><span className="attendance-calendar-view"><button type="button" className={calendarView === 'month' ? 'active' : ''} onClick={() => setCalendarView('month')}>Month</button><button type="button" className={calendarView === 'week' ? 'active' : ''} onClick={() => setCalendarView('week')}>Week</button></span></div></div><AttendanceCalendarGrid data={calendar} view={calendarView} onCorrect={openCorrection} /></Card>}
        </>)}
      </div>
    </Card>

    {faceOpen && <FaceScanner mode={faceMode} onClose={() => setFaceOpen(false)} onComplete={(token) => void onFaceComplete(token)} />}
    <Modal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} title="Check out" description="Face and trusted-site verification are complete. The handoff note is optional." width="lg" footer={<><Button variant="secondary" onClick={() => setCheckoutOpen(false)}>Cancel</Button><Button type="submit" form="checkout-form" busy={busy}>Complete check-out</Button></>}><form id="checkout-form" onSubmit={checkout}><Field label="Work summary (optional)"><textarea name="workLog" rows={3} placeholder="Summarize useful outcomes if you want to add a daily handoff." /></Field><FormGrid><Field label="Completed"><textarea name="completedItems" rows={2} /></Field><Field label="Still pending"><textarea name="pendingItems" rows={2} /></Field></FormGrid><Field label="Blockers"><textarea name="blockers" rows={2} /></Field></form></Modal>
    <Modal open={correctionOpen} onClose={() => { setCorrectionOpen(false); setCorrectionDefaults(null); }} title="Request attendance correction" description="A manager must approve this before payroll uses the corrected time. These in-page selectors avoid the unstable Android system picker." footer={<><Button variant="secondary" onClick={() => { setCorrectionOpen(false); setCorrectionDefaults(null); }}>Cancel</Button><Button type="submit" form="correction-form" busy={busy}>Submit request</Button></>}><form id="correction-form" key={`${correctionDefaults?.date || 'new'}-${correctionDefaults?.sessionId || 0}`} onSubmit={requestCorrection}><Field label="Attendance date"><WebDateField name="date" required defaultValue={correctionDefaults?.date || ''} /></Field><FormGrid><Field label="Correct check-in"><WebDateTimeField name="checkinAt" required defaultValue={correctionDefaults?.checkinAt || ''} /></Field><Field label="Correct check-out"><WebDateTimeField name="checkoutAt" required defaultValue={correctionDefaults?.checkoutAt || ''} /></Field></FormGrid><Field label="Reason"><textarea name="reason" required rows={3} defaultValue={correctionDefaults?.sessionId ? 'Missed check-out correction' : ''} /></Field></form></Modal>
  </div>;
}

function AttendanceCalendarGrid({ data, view, onCorrect }: {
  data: AttendanceCalendarData; view: 'month' | 'week'; onCorrect: (day: AttendanceCalendarDay) => void;
}) {
  const todayValue = new Date().toLocaleDateString('en-CA');
  const firstWeekday = new Date(`${data.month}-01T00:00:00`).getDay();
  let visibleDays = data.days;
  let leading = firstWeekday;
  if (view === 'week') {
    const selectedIndex = Math.max(0, data.days.findIndex((day) => day.date === todayValue));
    const selectedWeekday = new Date(`${data.days[selectedIndex]?.date || `${data.month}-01`}T00:00:00`).getDay();
    const startIndex = Math.max(0, selectedIndex - selectedWeekday);
    visibleDays = data.days.slice(startIndex, startIndex + 7);
    leading = new Date(`${visibleDays[0]?.date || `${data.month}-01`}T00:00:00`).getDay();
  }
  const correctionStatuses = new Set(['absent', 'checkout_pending', 'checkin_pending', 'correction_pending']);
  const shortLabels: Record<string, string> = {
    present: 'P', absent: 'A', off: 'Off', correction_pending: 'Correction pending',
    checkout_pending: 'Check-out pending', checkin_pending: 'Check-in pending', scheduled: 'Scheduled',
  };
  return <><div className="attendance-calendar-legend"><span className="present">P · Present</span><span className="absent">A · Absent</span><span className="off">Off</span><span className="correction_pending">Correction pending</span><span className="checkout_pending">Check-out pending</span><span className="checkin_pending">Check-in pending</span></div><div className="attendance-calendar-grid">
    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => <strong className="attendance-calendar-grid__weekday" key={label}>{label}</strong>)}
    {Array.from({ length: leading }, (_, index) => <span className="attendance-calendar-grid__blank" key={`blank-${index}`} />)}
    {visibleDays.map((day) => <button type="button" key={day.date} className={`attendance-calendar-day attendance-calendar-day--${day.status}${day.date === todayValue ? ' is-today' : ''}`} title={day.label} onClick={() => correctionStatuses.has(day.status) && onCorrect(day)} disabled={!correctionStatuses.has(day.status)}><span>{Number(day.date.slice(8))}</span><strong>{shortLabels[day.status] || day.label}</strong><small>{day.label}</small></button>)}
  </div></>;
}

function FaceScanner({ mode, onClose, onComplete }: { mode: 'enroll' | 'checkin' | 'checkout'; onClose: () => void; onComplete: (token?: string) => void }) {
  useModalScrollLock(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const faceApiRef = useRef<typeof import('@vladmandic/face-api') | null>(null);
  const scannerRunRef = useRef(0);
  const scanSessionIdRef = useRef(globalThis.crypto?.randomUUID?.() || `face-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const resumeCameraRef = useRef(false);
  const [status, setStatus] = useState('Preparing secure face scanner…');
  const [modelsReady, setModelsReady] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [modelAttempt, setModelAttempt] = useState(0);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameras, setCameras] = useState<ReturnType<typeof rankVideoInputs>>([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [frontCamera, setFrontCamera] = useState(true);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [siteProof, setSiteProof] = useState<{ method: 'location'; latitude: number; longitude: number; accuracy: number } | null>(null);
  const reportDiagnostic = useCallback((stage: string, outcome: 'started' | 'failed' | 'success', error?: unknown,
    extra: Record<string, unknown> = {}) => {
    const message = error instanceof Error ? error.message : error ? String(error) : '';
    void post('/api/face/diagnostics', {
      action: mode, stage, outcome, code: error ? faceScannerFailureCode(error, stage) : undefined,
      message, scanSessionId: scanSessionIdRef.current,
      appContext: window.NosivoMonitoring ? 'native_webview' : 'web_portal', ...extra,
    }).catch(() => undefined);
  }, [mode]);
  const stop = useCallback(() => {
    scannerRunRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingModels(true); setModelsReady(false); setReady(false); setError(''); setStatus('Preparing secure face scanner…');
      try {
        const faceapi = await loadFaceScannerModels();
        faceApiRef.current = faceapi;
        if (!active) return;
        setModelsReady(true); setStatus('Scanner ready. Tap Start camera to allow camera access.');
      } catch (scannerError) {
        if (!active) return;
        reportDiagnostic('model_load', 'failed', scannerError);
        setError((scannerError as Error).message || 'The secure scanner could not load.'); setStatus('Scanner unavailable.');
      } finally { if (active) setLoadingModels(false); }
    })();
    return () => { active = false; stop(); };
  }, [modelAttempt, reportDiagnostic, stop]);

  async function startCamera(preferredDeviceId = '') {
    if (!modelsReady || cameraStarting) return;
    stop(); const runId = scannerRunRef.current;
    setCameraStarting(true); setReady(false); setError(''); setSiteProof(null);
    setStatus(mode === 'enroll' ? 'Requesting camera permission…' : 'Starting camera and trusted-site check…');
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Camera access is not supported in this app view.');
      const locationRequest: Promise<{ position: GeolocationPosition | null; error: Error | null }> = mode === 'enroll'
        ? Promise.resolve({ position: null, error: null }) : (() => {
        if (!navigator.geolocation) return Promise.resolve({ position: null, error: new Error('Location access is required for attendance.') });
        return withScannerTimeout(new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(
          resolve, reject, { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
        )), 12_000, 'Location permission did not respond. Check the Nosivo location permission and try again.')
          .then((position) => ({ position, error: null }))
          .catch((locationError) => ({ position: null, error: new Error(locationErrorMessage(locationError)) }));
      })();
      const requestCamera = async (video: MediaTrackConstraints) => {
        const cameraRequest = navigator.mediaDevices.getUserMedia({ video, audio: false });
        try {
          return await withScannerTimeout(cameraRequest, 15_000,
            'Camera permission did not respond. Open device Settings > Nosivo (or Apps > Nosivo) > Camera, choose Allow, then return and tap Try camera again.');
        } catch (cameraError) {
          if (cameraError instanceof FaceScannerTimeoutError) {
            void cameraRequest.then((lateStream) => lateStream.getTracks().forEach((track) => track.stop()), () => undefined);
          }
          throw cameraError;
        }
      };
      const idealFrontCamera = { facingMode: { ideal: 'user' }, width: { ideal: 1280 }, height: { ideal: 720 }, aspectRatio: { ideal: 4 / 3 } };
      let stream = await requestCamera(idealFrontCamera);
      let availableCameras: ReturnType<typeof rankVideoInputs> = [];
      try {
        availableCameras = rankVideoInputs(await navigator.mediaDevices.enumerateDevices());
      } catch { availableCameras = []; }
      const selectedCamera = (preferredDeviceId
        ? availableCameras.find((camera) => camera.deviceId === preferredDeviceId)
        : availableCameras[0]) || null;
      const initialDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId || '';
      if (selectedCamera?.deviceId && selectedCamera.deviceId !== initialDeviceId) {
        stream.getTracks().forEach((track) => track.stop());
        try {
          stream = await requestCamera({ deviceId: { exact: selectedCamera.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, aspectRatio: { ideal: 4 / 3 } });
        } catch (selectedCameraError) {
          if (preferredDeviceId) throw selectedCameraError;
          stream = await requestCamera(idealFrontCamera);
        }
      }
      if (scannerRunRef.current !== runId) { stream.getTracks().forEach((track) => track.stop()); return; }
      streamRef.current = stream;
      if (!videoRef.current) throw new Error('The camera preview could not be opened. Tap Try camera again.');
      const activeTrack = stream.getVideoTracks()[0];
      const activeSettings = activeTrack?.getSettings() || {};
      const actualDeviceId = activeSettings.deviceId || selectedCamera?.deviceId || '';
      const actualCamera = availableCameras.find((camera) => camera.deviceId === actualDeviceId) || selectedCamera;
      const facing = actualCamera?.facing || cameraFacingFromLabel(activeTrack?.label || '');
      const isFront = facing === 'user' || (facing !== 'environment' && activeSettings.facingMode !== 'environment');
      setCameras(availableCameras); setActiveDeviceId(actualDeviceId); setFrontCamera(isFront);
      videoRef.current.classList.toggle('is-front-camera', isFront);
      videoRef.current.srcObject = stream;
      await withScannerTimeout(videoRef.current.play(), 10_000, 'The camera preview took too long to start. Tap Try camera again.');
      await waitForVideoReady(videoRef.current);
      if (mode !== 'enroll') {
        setStatus('Confirming that you are at a trusted work site…');
        const location = await locationRequest;
        if (location.error) throw location.error;
        const position = location.position;
        if (!position) throw new Error('Location access is required for attendance.');
        if (scannerRunRef.current !== runId) return;
        setSiteProof({ method: 'location', latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy });
      }
      setStatus(mode === 'enroll' ? 'Face the front camera and fit your face inside the circle.'
        : 'Trusted site ready. Fit your face inside the circle.'); setReady(true);
    } catch (scannerError) {
      if (scannerRunRef.current !== runId) return;
      reportDiagnostic('camera_or_location', 'failed', scannerError, {
        cameraLabel: streamRef.current?.getVideoTracks()[0]?.label || null,
        locationAccuracy: siteProof?.accuracy,
      });
      streamRef.current?.getTracks().forEach((track) => track.stop()); streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      setError(cameraErrorMessage(scannerError)); setStatus('Camera unavailable.');
    } finally { if (scannerRunRef.current === runId) setCameraStarting(false); }
  }

  useEffect(() => {
    if (modelsReady && document.visibilityState === 'visible' && !streamRef.current) void startCamera();
  }, [modelAttempt, modelsReady]);

  function switchCamera() {
    if (cameras.length < 2 || cameraStarting || busy) return;
    const activeIndex = cameras.findIndex((camera) => camera.deviceId === activeDeviceId);
    const nextCamera = cameras[(activeIndex + 1 + cameras.length) % cameras.length];
    if (nextCamera) void startCamera(nextCamera.deviceId);
  }

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        resumeCameraRef.current = true; stop(); setCameraStarting(false); setReady(false); setStatus('Camera paused while Nosivo is in the background.');
      } else if (document.visibilityState === 'visible' && resumeCameraRef.current && modelsReady && !busy) {
        resumeCameraRef.current = false; window.setTimeout(() => void startCamera(activeDeviceId), 120);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeDeviceId, busy, modelsReady, stop]);

  async function scan() {
    const faceapi = faceApiRef.current; const video = videoRef.current; if (!faceapi || !video) return;
    setBusy(true); setError('');
    try {
      const descriptorSamples: number[][] = [];
      const directions = ['Look straight at the camera.', 'Turn your face slightly left.', 'Turn your face slightly right.'];
      for (let sampleIndex = 0; sampleIndex < 3; sampleIndex += 1) {
        let descriptor: Float32Array | null = null;
        let lastGuidance = directions[sampleIndex];
        const deadline = Date.now() + 7_000;
        let detectionAttempts = 0;
        while (!descriptor && Date.now() < deadline) {
          detectionAttempts += 1;
          setStatus(`Sample ${sampleIndex + 1} of 3 · ${lastGuidance}`);
          const lightCanvas = document.createElement('canvas');
          lightCanvas.width = 32; lightCanvas.height = 32;
          const lightContext = lightCanvas.getContext('2d', { willReadFrequently: true });
          let brightness: number | null = null;
          if (lightContext) {
            lightContext.drawImage(video, 0, 0, 32, 32);
            const pixels = lightContext.getImageData(0, 0, 32, 32).data;
            let total = 0;
            for (let index = 0; index < pixels.length; index += 4) {
              total += (pixels[index] * 0.2126) + (pixels[index + 1] * 0.7152) + (pixels[index + 2] * 0.0722);
            }
            brightness = total / (pixels.length / 4);
          }
          if (brightness !== null && brightness < 24) {
            lastGuidance = 'Move toward a light source; your face is too dark.';
          } else if (brightness !== null && brightness > 246) {
            lastGuidance = 'Move away from the bright light behind or above you.';
          } else {
            const results = await faceapi.detectAllFaces(video,
              new faceapi.TinyFaceDetectorOptions(detectionAttempts < 10
                ? { inputSize: 320, scoreThreshold: 0.48 }
                : { inputSize: 416, scoreThreshold: 0.35 }))
              .withFaceLandmarks().withFaceDescriptors();
            if (results.length > 1) lastGuidance = 'Only one person should be visible in the camera.';
            else if (!results.length) lastGuidance = 'Keep your full face visible and improve the lighting.';
            else {
              const quality = faceFrameQuality(results[0].detection.box, video.videoWidth, video.videoHeight);
              if (!quality.ok) lastGuidance = quality.message;
              else descriptor = results[0].descriptor;
            }
          }
          if (!descriptor) await new Promise((resolve) => window.setTimeout(resolve, 160));
        }
        if (!descriptor) throw new Error(lastGuidance || 'No clear face was found. Improve the lighting and try again.');
        descriptorSamples.push(Array.from(descriptor));
        if (sampleIndex < 2) await new Promise((resolve) => window.setTimeout(resolve, 280));
      }
      const canvas = document.createElement('canvas'); canvas.width = video.videoWidth || 640; canvas.height = video.videoHeight || 480; canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      const snapshot = canvas.toDataURL('image/jpeg', 0.78);
      const scanMetadata = { scanSessionId: scanSessionIdRef.current, cameraLabel: activeCameraLabel,
        appContext: window.NosivoMonitoring ? 'native_webview' : 'web_portal' };
      if (mode === 'enroll') {
        setStatus('Encrypting employee face profile…'); await post('/api/face/enroll', { descriptorSamples, ...scanMetadata });
        setStatus('Face ID enrollment complete.'); stop(); window.setTimeout(() => onComplete(), 500);
      } else {
        if (!siteProof) throw new Error('Trusted-site location is not ready. Allow location access and try again.');
        setStatus('Matching employee face and trusted site…'); const result = await post<{ token: string }>('/api/face/verify',
          { action: mode, descriptorSamples, snapshot, siteProof, ...scanMetadata });
        setStatus('Face and site verified.'); stop(); window.setTimeout(() => onComplete(result.token), 350);
      }
    } catch (scannerError) {
      reportDiagnostic('capture_or_match', 'failed', scannerError, { cameraLabel: activeCameraLabel,
        locationAccuracy: siteProof?.accuracy });
      setError((scannerError as Error).message); setStatus('Face scan was not completed.'); setBusy(false);
    }
  }

  const retryModels = () => { faceApiRef.current = null; resetFaceScannerModels(); setModelAttempt((attempt) => attempt + 1); };
  const primaryLabel = ready ? (mode === 'enroll' ? 'Save employee Face ID' : 'Scan face')
    : modelsReady ? (error ? 'Try camera again' : 'Start camera')
      : error ? 'Retry scanner' : 'Preparing scanner…';
  const primaryAction = ready ? () => void scan() : modelsReady ? () => void startCamera() : retryModels;
  const activeCameraLabel = cameras.find((camera) => camera.deviceId === activeDeviceId)?.label
    || (frontCamera ? 'Front camera' : 'Rear camera');

  return <div className="face-modal"><div className="face-dialog">
    <header><div><span className="eyebrow">Employee attendance</span><h2>{mode === 'enroll' ? 'Set up employee Face ID' : `Scan to check ${mode === 'checkin' ? 'in' : 'out'}`}</h2></div>
      <button onClick={() => { stop(); onClose(); }} aria-label="Close">×</button></header>
    <div className="face-camera">
      <video ref={videoRef} className={frontCamera ? 'is-front-camera' : ''} muted playsInline autoPlay />
      <div className="face-guide"><span /><span /><span /><span /></div>
      {ready && cameras.length > 1 && <button type="button" className="face-camera-switch" onClick={switchCamera}
        disabled={cameraStarting || busy} title={activeCameraLabel}><RefreshCw size={14} />Switch camera</button>}
      {!ready && <div className="face-loading" role="status" aria-live="polite"><Camera size={23} /><span>{error || status}</span></div>}
    </div>
    <div className="face-scan-status" aria-live="polite">
      <span className={error ? 'face-scan-status__icon face-scan-status__icon--error' : ready ? 'face-scan-status__icon face-scan-status__icon--ready' : 'face-scan-status__icon'}>{ready ? <ScanFace size={18} /> : <Camera size={18} />}</span>
      <div><strong>{error || status}</strong><small>{mode === 'enroll' ? `${activeCameraLabel} · encrypted template is linked only to this employee.`
        : siteProof ? <><MapPin size={11} /> Location accuracy {Math.round(siteProof.accuracy)} m · {activeCameraLabel}</> : 'Camera and location permissions are required.'}</small></div>
    </div>
    <footer><Button variant="secondary" onClick={() => { stop(); onClose(); }}>Cancel</Button>
      <Button icon={ready ? ScanFace : Camera} disabled={!ready && !modelsReady && !error}
        busy={busy || loadingModels || cameraStarting} onClick={primaryAction}>{primaryLabel}</Button></footer>
  </div></div>;
}
